import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import DateSelect from '../components/ui/DateSelect';
import { useStudentStore } from '../store/StudentStore';
import { useUser } from '../store/UserContext';

export default function StudentCertificateCreatePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { controller } = useStudentStore();
  const { user } = useUser();
  const student = controller.getStudent(id);

  const [form, setForm] = useState({
    name: '',
    issueDate: new Date().toISOString().slice(0, 10),
    expiryDate: '',
    file: null
  });
  const [formError, setFormError] = useState('');

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!student) return;
    if (!form.name.trim()) {
      setFormError('Certificate name is required.');
      return;
    }
    if (!form.issueDate) {
      setFormError('Issue date is required.');
      return;
    }

    controller.addCertificate(
      student.id,
      {
        name: form.name.trim(),
        issueDate: form.issueDate,
        expiryDate: form.expiryDate || null,
        fileName: form.file ? form.file.name : ''
      },
      user
    );

    navigate(`/students/${student.id}/profile?tab=certificates`);
  };

  if (!student) {
    return (
      <Card className="p-6">
        <p className="sp-muted">Student not found.</p>
        <Button variant="secondary" className="mt-4" onClick={() => navigate('/students')}>
          Back to students
        </Button>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="sp-value" style={{ fontSize: '20px', fontWeight: 600 }}>Add Certificate</h2>
          <p className="sp-muted">Record certificate details and optional expiry date.</p>
        </div>
        <Button variant="secondary" onClick={() => navigate(`/students/${student.id}/profile?tab=certificates`)}>
          Back to certificates
        </Button>
      </div>

      <Card className="p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          {formError && <div className="sp-panel sp-panel--muted p-3 text-sm">{formError}</div>}

          <div>
            <label className="sp-label">Certificate name</label>
            <input
              className="sp-input mt-2 w-full"
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
              placeholder="e.g. First Aid Level 1"
              required
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="sp-label">Issue date</label>
              <DateSelect
                value={form.issueDate}
                onChange={(value) => setForm({ ...form, issueDate: value })}
                className="mt-2 w-full"
              />
            </div>
            <div>
              <label className="sp-label">Expiry date (optional)</label>
              <DateSelect
                value={form.expiryDate}
                onChange={(value) => setForm({ ...form, expiryDate: value })}
                className="mt-2 w-full"
              />
            </div>
          </div>

          <div>
            <label className="sp-label">Upload certificate (optional)</label>
            <input
              type="file"
              className="sp-input mt-2 w-full"
              onChange={(event) => setForm({ ...form, file: event.target.files?.[0] || null })}
            />
            {form.file && (
              <p className="text-xs sp-muted mt-1">{form.file.name}</p>
            )}
          </div>

          <div className="flex items-center justify-end gap-2">
            <Button
              variant="secondary"
              type="button"
              onClick={() => navigate(`/students/${student.id}/profile?tab=certificates`)}
            >
              Cancel
            </Button>
            <Button type="submit">Save Certificate</Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
